/**
 * Peças compartilhadas pelos diálogos das páginas de planejamento e dívidas: segmentado, cor e
 * ícone, categoria + subcategoria, chips de múltipla escolha, editor de cronograma e o toast com
 * Desfazer. Mesmo visual dos diálogos de Contas e Cartões (classes `acc-*`).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { Model } from '../viewer/lib/model';
import type { ScheduleLine } from '../viewer/lib/editing';
import { InstitutionLogo } from '../viewer/components/InstitutionLogo';
import type { AccountRow, CategoryRow } from '../viewer/lib/types';
import { CategoryIcon } from '../viewer/components/CategoryIcon';
import { TOAST_MS, catChip, fmtAmount, parseAmount, parseDay } from '../viewer/components/sheet';
import { addMonths, brl } from '../viewer/lib/format';
import { Picker, type PickerOption } from './Picker';
import { field, label } from './ui';

const pad = (n: number) => String(n).padStart(2, '0');
/** Dia LOCAL (não UTC), como o app grava. */
export const todayLocal = () => { const d = new Date(); return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; };
export const toDDMMYYYY = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`;
export const round2 = (v: number) => Math.round(v * 100) / 100;
/** "8,99" → 8.99; aceita ponto; null se não for número. */
export function parseRate(raw: string): number | null {
  const s = raw.trim().replace('%', '').replace(',', '.');
  if (!s) return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}
/** Inteiro positivo ("12") ou null. */
export function parseInt1(raw: string): number | null {
  const n = Number(raw.trim());
  return Number.isInteger(n) && n >= 1 ? n : null;
}

/** A mesma paleta do seletor de cor do app (components/ui/ColorPicker.tsx). */
export const PRESET_COLORS = [
  '#2B697A', '#4DAFB8', '#1A4350', '#E8927C', '#34D399', '#10B981', '#F87171', '#EF4444', '#FBBF24', '#F59E0B',
  '#60A5FA', '#3B82F6', '#A78BFA', '#8B5CF6', '#F472B6', '#EC4899', '#6B7280', '#374151', '#FB923C', '#14B8A6',
];
export const PLAN_ICONS = [
  'target', 'piggy-bank', 'shield', 'airplane', 'house', 'car', 'motorcycle', 'bicycle', 'laptop', 'device-mobile', 'graduation-cap', 'book-open',
  'gift', 'heart', 'baby', 'dog', 'cat', 'suitcase-rolling', 'tent', 'mountains', 'camera', 'game-controller', 'barbell', 'couch',
  'paint-roller', 'wrench', 'wallet', 'vault', 'trophy', 'star', 'confetti', 'sparkle',
];
export const BUDGET_ICONS = [
  'chart-pie', 'fork-knife', 'shopping-cart', 'basket', 'car', 'gas-pump', 'bus', 'house', 'lightbulb', 'confetti', 'popcorn', 'game-controller',
  'heart', 'pill', 'stethoscope', 't-shirt', 'handbag', 'graduation-cap', 'books', 'laptop', 'gift', 'coffee', 'beer', 'airplane',
  'dog', 'baby', 'briefcase', 'money', 'wallet', 'target', 'tag', 'sparkle',
];

/** Botões lado a lado, um só ligado (o SegmentedControl do app). */
export function Seg<T extends string>({ options, value, onChange, testId, disabled }: {
  options: Array<{ value: T; label: string; hint?: string }>; value: T; onChange: (v: T) => void; testId?: string; disabled?: boolean;
}) {
  return (
    <div className="acc-seg" role="radiogroup">
      {options.map(o => (
        <button key={o.value} type="button" role="radio" aria-checked={value === o.value} className={`acc-chip ${value === o.value ? 'is-on' : ''}`} disabled={disabled}
          onClick={() => onChange(o.value)} data-testid={testId ? `${testId}-${o.value}` : undefined} title={o.hint}>
          <span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Cor (bolinhas) e ícone (grade) como o form de meta/orçamento do app. */
export function IconColor({ icon, color, icons, onIcon, onColor, testId }: { icon: string; color: string; icons: string[]; onIcon: (i: string) => void; onColor: (c: string) => void; testId?: string }) {
  return (
    <div className="acc-dlg-row">
      <div className="acc-dlg-group is-grow">
        <span className={label}>Cor</span>
        <div className="pick-colors">
          {PRESET_COLORS.map(c => <button key={c} type="button" aria-label={c} aria-pressed={color === c} className={color === c ? 'is-on' : ''} style={{ background: c }} onClick={() => onColor(c)} />)}
        </div>
      </div>
      <div className="acc-dlg-group is-grow">
        <span className={label}>Ícone</span>
        <div className="pick-icons" data-testid={testId}>
          {icons.map(i => (
            <button key={i} type="button" aria-label={i} aria-pressed={icon === i} className={icon === i ? 'is-on' : ''} style={icon === i ? { color, background: `color-mix(in srgb, ${color} 16%, transparent)`, borderColor: color } : undefined} onClick={() => onIcon(i)}>
              <CategoryIcon name={i} size={16} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** Categoria (de um tipo) + subcategoria dela, com os ícones das categorias. */
export function CategoryFields({ model, type, categoryId, subcategoryId, onCategory, onSubcategory, testId, subLabel = 'Subcategoria' }: {
  model: Model; type: 'expense' | 'income'; categoryId: string; subcategoryId: string; onCategory: (id: string) => void; onSubcategory: (id: string) => void; testId: string; subLabel?: string;
}) {
  const cats: PickerOption[] = [...model.categories.values()].filter(c => c.type === type && !c.isArchived).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
    .map(c => ({ id: c.id, label: c.name, icon: catChip(c.color, c.icon) }));
  const subs: PickerOption[] = [...model.subcategories.values()].filter(s => s.categoryId === categoryId).sort((a, b) => a.name.localeCompare(b.name, 'pt-BR')).map(s => ({ id: s.id, label: s.name }));
  return (
    <div className="acc-dlg-row">
      <div className="acc-dlg-group is-grow"><span className={label}>Categoria</span><Picker value={categoryId} options={cats} onChange={id => { onCategory(id); onSubcategory(''); }} placeholder="Escolha a categoria" testId={testId} /></div>
      {subs.length > 0 && <div className="acc-dlg-group is-grow"><span className={label}>{subLabel}</span><Picker value={subcategoryId} options={subs} onChange={onSubcategory} emptyLabel="Nenhuma" testId={`${testId}-sub`} /></div>}
    </div>
  );
}

export interface ChipOpt { id: string; label: string; icon?: ReactNode; hint?: string }
/** Vários ligados ao mesmo tempo (categorias do orçamento, origens). */
export function MultiChips({ options, values, onToggle, testId }: { options: ChipOpt[]; values: Set<string>; onToggle: (id: string) => void; testId: string }) {
  return (
    <div className="acc-chips">
      {options.map(o => (
        <button key={o.id} type="button" aria-pressed={values.has(o.id)} className={`acc-chip ${values.has(o.id) ? 'is-on' : ''}`} onClick={() => onToggle(o.id)} data-testid={`${testId}-option`} data-value={o.id} title={o.hint}>
          {o.icon}<span>{o.label}</span>
        </button>
      ))}
    </div>
  );
}

/** Cronograma de recebimento: N linhas de data + valor que precisam somar o alvo (ADR 0036). */
export function ScheduleEditor({ lines, onChange, target, refDate, testId }: { lines: ScheduleLine[]; onChange: (l: ScheduleLine[]) => void; target: number; refDate: string; testId: string }) {
  const [raw, setRaw] = useState(() => lines.map(l => ({ date: l.date ? toDDMMYYYY(l.date) : '', amount: l.amount ? fmtAmount(l.amount) : '' })));
  const emit = (next: typeof raw) => {
    setRaw(next);
    onChange(next.map(r => ({ date: parseDay(r.date, refDate) ?? '', amount: parseAmount(r.amount) ?? 0 })));
  };
  const sum = round2(raw.reduce((s, r) => s + (parseAmount(r.amount) ?? 0), 0));
  const diff = round2(target - sum);
  const add = () => {
    const last = raw.length ? parseDay(raw[raw.length - 1].date, refDate) : null;
    const next = last ? `${addMonths(last.slice(0, 7), 1)}-${last.slice(8, 10)}` : `${addMonths(refDate.slice(0, 7), 1)}-${refDate.slice(8, 10)}`;
    emit([...raw, { date: toDDMMYYYY(next), amount: diff > 0 ? fmtAmount(diff) : '' }]);
  };
  const split = () => {
    if (!raw.length || target <= 0) return;
    const each = Math.floor((target / raw.length) * 100) / 100;
    const rest = round2(target - each * (raw.length - 1));
    emit(raw.map((r, i) => ({ ...r, amount: fmtAmount(i === raw.length - 1 ? rest : each) })));
  };
  return (
    <div className="sched" data-testid={testId}>
      {raw.map((r, i) => (
        <div key={i} className="sched-row">
          <small>{i + 1}</small>
          <input className={`${field} tnum`} placeholder="dd/mm/aaaa" value={r.date} onChange={e => emit(raw.map((x, j) => (j === i ? { ...x, date: e.target.value } : x)))} data-testid={`${testId}-date`} />
          <input className={`${field} tnum`} inputMode="decimal" placeholder="0,00" value={r.amount} onChange={e => emit(raw.map((x, j) => (j === i ? { ...x, amount: e.target.value } : x)))} data-testid={`${testId}-amount`} />
          <button type="button" className="acc-btn is-small" aria-label="Remover" onClick={() => emit(raw.filter((_, j) => j !== i))}>✕</button>
        </div>
      ))}
      <div className="acc-dlg-foot" style={{ justifyContent: 'flex-start' }}>
        <button type="button" className="acc-btn is-small" onClick={add} data-testid={`${testId}-add`}><CategoryIcon name="plus" size={12} /> Recebimento</button>
        {raw.length > 1 && <button type="button" className="acc-btn is-small" onClick={split}>Dividir igual</button>}
        <span className={`acc-dlg-note tnum ${Math.abs(diff) > 0.004 ? 'text-neg' : ''}`}>{raw.length ? `soma ${brl(sum)} de ${brl(target)}${Math.abs(diff) > 0.004 ? ` · ${diff > 0 ? 'faltam' : 'passam'} ${brl(Math.abs(diff))}` : ' · fecha'}` : 'Nenhum recebimento ainda.'}</span>
      </div>
    </div>
  );
}

/** Toast da página com Desfazer, igual ao de Contas/Cartões. */
export function useToastState() {
  const [toast, setToast] = useState<{ msg: string; undo?: () => Promise<void> } | null>(null);
  const timer = useRef<number | null>(null);
  useEffect(() => () => { if (timer.current) window.clearTimeout(timer.current); }, []);
  const show = (msg: string, undo?: () => Promise<void>) => {
    if (timer.current) window.clearTimeout(timer.current);
    setToast({ msg, undo });
    timer.current = window.setTimeout(() => setToast(null), TOAST_MS);
  };
  return { toast, show, clear: () => setToast(null) };
}
export function Toast({ toast, testId, onUndo }: { toast: { msg: string; undo?: () => Promise<void> } | null; testId: string; onUndo: (u: () => Promise<void>) => void }) {
  if (!toast) return null;
  return (
    <div className="sheet-toast" role="status" data-testid={testId}>
      <span>{toast.msg}</span>
      {toast.undo && <button type="button" onClick={() => onUndo(toast.undo!)}>Desfazer</button>}
    </div>
  );
}

/** Botão da barra de ações da página. */
export function ActionBtn({ onClick, icon, children, testId, primary, danger, disabled }: { onClick: () => void; icon?: string; children: ReactNode; testId?: string; primary?: boolean; danger?: boolean; disabled?: boolean }) {
  return (
    <button type="button" className={`acc-btn ${primary ? 'is-primary' : ''} ${danger ? 'is-danger-text' : ''}`} onClick={onClick} data-testid={testId} disabled={disabled}>
      {icon && <CategoryIcon name={icon} size={14} />}{children}
    </button>
  );
}

// ── opções dos seletores de um item só (OnePicker): conta com o logo, categoria com o ícone na cor ──
export const ACCOUNT_KIND: Record<string, string> = { checking: 'Conta corrente', savings: 'Poupança', wallet: 'Dinheiro', digital: 'Conta digital', investment: 'Investimento', other: 'Conta' };
export const accountLogo = (a: AccountRow, size = 28) => <InstitutionLogo icon={a.institutionIcon} institution={a.institution} name={a.name} color={a.color} size={size} />;
export const accountOption = (a: AccountRow) => ({ id: a.id, name: a.name, sub: ACCOUNT_KIND[a.type] ?? 'Conta', icon: accountLogo(a) });
export const categoryMark = (c: CategoryRow) => <i style={{ color: c.color, background: `color-mix(in srgb, ${c.color} 14%, transparent)` }}><CategoryIcon name={c.icon} size={13} /></i>;
export const categoryOption = (c: CategoryRow) => ({ id: c.id, name: c.name, icon: categoryMark(c) });
